import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore } from '@/stores/authStore';
import type { DepartmentDto } from '@/types/department';

import styles from './DepartmentModals.module.scss';

interface ViewDepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DepartmentDto | null;
  onEdit: () => void;
  onDelete: () => void;
  onAudit?: () => void;
}

function StatusBadge({ status }: { status: DepartmentDto['status'] }) {
  const className = status === 'ACTIVE' ? styles.statusActive : styles.statusInactive;
  return <span className={`${styles.statusBadge} ${className}`}>{status}</span>;
}

function ViewDepartmentModal({
  isOpen,
  onClose,
  data,
  onEdit,
  onDelete,
  onAudit,
}: ViewDepartmentModalProps) {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  if (!data) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={data.name}
      size="md"
      footer={
        <div className={styles.viewFooter}>
          {hasPermission('DEPARTMENTS_DELETE') && data.status === 'ACTIVE' && (
            <Button variant="danger" size="sm" onClick={onDelete}>
              {t('K_DEACTIVATE', 'Deactivate')}
            </Button>
          )}
          <div className={styles.viewFooterRight}>
            <Button variant="outline" size="sm" onClick={onClose}>
              {t('K_CLOSE', 'Close')}
            </Button>
            {onAudit && hasPermission('AUDIT_READ') && (
              <Button variant="outline" size="sm" onClick={onAudit}>
                {t('K_AUDIT_TITLE', 'Audit trail')}
              </Button>
            )}
            {hasPermission('DEPARTMENTS_UPDATE') && data.status !== 'INACTIVE' && (
              <Button variant="filled" size="sm" onClick={onEdit}>
                {t('K_EDIT', 'Edit')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className={styles.fieldGrid}>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_NAME', 'Name')}</span>
          <span className={styles.fieldValue}>{data.name}</span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_CODE', 'Code')}</span>
          <span className={styles.fieldValue}>{data.code}</span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_STATUS', 'Status')}</span>
          <StatusBadge status={data.status} />
        </div>
      </div>
    </Modal>
  );
}

export default ViewDepartmentModal;
